import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, MessageCircle, X } from "lucide-react";
import { Reveal } from "./Reveal";
import { NEXT_ASSESSMENT_URL, WHATSAPP_URL } from "../constants";
import { PolicyModal } from "./PolicyModal";

const ZOHO_FORM_URL =
  "https://forms.zohopublic.in/movementoreducation/form/StudentEnquiryFormtest/formperma/ilsYLofH_ZjveeOmsKD5-gBwzdjnUlNX-2hugwiApYw?zf_rszfm=1";

export const Contact = () => {
  const [isZohoOpen, setIsZohoOpen] = useState(false);
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);
  const policyButtonRef = useRef(null);

  useEffect(() => {
    if (!isZohoOpen) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsZohoOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isZohoOpen]);

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative pt-12 lg:pt-16 pb-20 lg:pb-28 bg-[#FFFFFF]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid min-w-0 w-full items-start gap-10 lg:items-stretch lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-12">
          <div className="min-w-0 self-start lg:h-full">
            <Reveal className="flex h-full min-w-0 flex-col">
              <p
                data-testid="contact-overline"
                className="text-xs sm:text-sm uppercase tracking-[0.3em] font-bold text-[#FE7815]"
              >
                Begin your journey
              </p>
              <h2
                data-testid="contact-heading"
                className="mt-6 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-[1.15] text-[#111111]"
              >
                Your next chapter starts with a conversation.
              </h2>

              <div className="mt-7 flex flex-col gap-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="group flex items-center justify-between gap-4 bg-[#111111] rounded-2xl p-5 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow] duration-300"
                >
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#22C55E]">
                      Need guidance?
                    </p>
                    <p className="mt-1.5 text-lg font-bold text-white">
                      Talk to a Mentor
                    </p>
                  </div>
                  <div className="relative w-12 h-12 shrink-0 rounded-full bg-[#22C55E] flex items-center justify-center pulse-ring">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                </a>

                <a
                  href={NEXT_ASSESSMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-next-button"
                  className="group flex items-center justify-between gap-4 bg-white border border-black/5 rounded-2xl p-5 hover:-translate-y-1 hover:shadow-xl hover:shadow-orange-500/10 transition-[transform,box-shadow] duration-300"
                >
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FE7815]">
                      Discover yourself first?
                    </p>
                    <p className="mt-1.5 text-lg font-bold text-[#111111]">
                      Explore NEXT
                    </p>
                  </div>
                  <div className="w-12 h-12 shrink-0 rounded-full bg-gradient-to-br from-[#FE7815] to-[#F01E1F] flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-white transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </a>
              </div>

              <div className="mt-6 h-44 w-full overflow-hidden rounded-3xl border border-[#111111]/10 bg-white shadow-[0_24px_60px_rgba(17,17,17,0.06)] lg:min-h-44 lg:flex-1">
                <iframe
                  title="Movementor registered address on Google Maps"
                  src="https://www.google.com/maps?q=ABHCON+Axis+Complex%2C+3F1%2C+High+Road%2C+South+Kuriachira%2C+Thrissur%2C+Kerala+680006&output=embed"
                  className="h-full w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          <div className="min-w-0 self-start lg:h-full">
            <Reveal delay={0.15}>
              <div
                data-testid="enquiry-form"
                className="relative max-w-full overflow-hidden rounded-3xl border border-[#111111]/10 bg-white p-7 shadow-[0_24px_60px_rgba(17,17,17,0.06)] lg:p-9"
              >
                <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#FE7815] to-[#F01E1F]" />
                <h3 className="text-xl sm:text-2xl font-semibold text-[#111111]">
                  Send us an enquiry
                </h3>
                <p className="mt-2 text-sm text-[#111111]">
                  Tell us a little about where you are — we'll guide the next
                  step.
                </p>
                <div className="mt-7 max-w-full rounded-2xl border border-[#111111]/10 bg-[#FFFFFF] p-6 sm:p-7">
                  <p className="text-sm leading-relaxed text-[#111111]">
                    Ready to take the next step? Complete our enquiry form and
                    our team will get back to you within 24 hours.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsZohoOpen(true)}
                    data-testid="enquiry-submit-button"
                    className="mt-6 inline-flex min-h-14 w-full items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-[#FE7815] to-[#F01E1F] px-8 py-4 text-base font-bold text-white transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-orange-500/30"
                  >
                    Open Enquiry Form
                    <ArrowUpRight className="h-5 w-5" />
                  </button>
                  <button
                    ref={policyButtonRef}
                    type="button"
                    onClick={() => setIsPolicyModalOpen(true)}
                    className="mt-4 inline-flex min-h-14 w-full items-center justify-center rounded-full border border-[#111111]/15 px-8 py-4 text-sm font-semibold text-[#111111] transition-[background-color,border-color] duration-300 hover:border-[#FE7815] hover:bg-[#FE7815]/5 focus:outline-none focus:ring-2 focus:ring-[#FE7815] focus:ring-offset-2"
                  >
                    View Policies
                  </button>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-6 min-h-44 overflow-hidden rounded-3xl border border-[#111111]/10 bg-white shadow-[0_24px_60px_rgba(17,17,17,0.06)]">
                <div className="p-7 sm:p-8">
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FE7815]">
                    Visit us
                  </p>
                  <h3 className="mt-2 text-xl font-semibold text-[#111111]">
                    Come Talk to Us
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#111111]">
                    Abhcon Axis Complex, 3F1, High Road, South Kuriachira,
                    Thrissur, Kerala 680006
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {isZohoOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#111111]/80 p-4 sm:p-6"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsZohoOpen(false);
          }}
        >
          <div
            className="relative flex h-[min(92vh,900px)] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="zoho-dialog-title"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-[#111111]/10 bg-white px-5 py-4 sm:px-7">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FE7815]">
                  Begin your journey
                </p>
                <h3
                  id="zoho-dialog-title"
                  className="mt-1 text-lg font-semibold text-[#111111]"
                >
                  Send us an enquiry
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsZohoOpen(false)}
                aria-label="Close enquiry form"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[#111111] transition-colors hover:bg-[#111111]/5 hover:text-[#F01E1F]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <iframe
              src={ZOHO_FORM_URL}
              title="Study Abroad: Your First Step Starts Here"
              className="h-[calc(92vh-77px)] w-full border-0 bg-white"
            />
          </div>
        </div>
      )}

      <PolicyModal
        isOpen={isPolicyModalOpen}
        onClose={() => setIsPolicyModalOpen(false)}
        triggerRef={policyButtonRef}
      />
    </section>
  );
};
